import React, { useState, useEffect } from 'react'
import { simpananBerjangkaService } from '../../services/simpananBerjangkaService'
import { useAuthStore } from '../../stores/authStore.js'
import SimpananBerjangkaForm from './SimpananBerjangkaForm'
import SimpananBerjangkaDetail from './SimpananBerjangkaDetail'

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '0'
  return new Intl.NumberFormat('id-ID').format(value)
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const TENOR_OPTIONS = [
  { value: 'SB_01', label: '1 Bulan' },
  { value: 'SB_03', label: '3 Bulan' },
  { value: 'SB_06', label: '6 Bulan' },
  { value: 'SB_12', label: '12 Bulan' },
  { value: 'SB_24', label: '24 Bulan' }
]

const STATUS_COLORS = {
  AKTIF: 'badge-success',
  LUNAS: 'badge-primary',
  DITUTUP: 'badge-danger'
}

function SimpananBerjangka() {
  const { user } = useAuthStore()
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)

  // Filters
  const [search, setSearch] = useState('')
  const [filterJenis, setFilterJenis] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  useEffect(() => {
    fetchAccounts()
  }, [filterJenis, filterStatus])

  const fetchAccounts = async () => {
    setLoading(true)
    try {
      const params = {}
      if (search) params.search = search
      if (filterJenis) params.kodeJenis = filterJenis
      if (filterStatus) params.status = filterStatus
      if (startDate) params.startDate = startDate
      if (endDate) params.endDate = endDate

      const res = await simpananBerjangkaService.getAccounts(params)
      const innerData = res.data?.data
      const accountsData = Array.isArray(innerData) ? innerData : (innerData?.data || [])
      setAccounts(accountsData)
    } catch (error) {
      console.error('Error fetching accounts:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    fetchAccounts()
  }

  const handleViewDetail = async (account) => {
    try {
      const res = await simpananBerjangkaService.getAccountDetail(account.Id)
      const innerData = res.data?.data
      setSelectedAccount(Array.isArray(innerData) ? innerData[0] : innerData)
      setShowDetail(true)
    } catch (error) {
      console.error('Error fetching detail:', error)
    }
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    fetchAccounts()
  }

  return (
    <div className="container-fluid p-3">
      <div className="card shadow border-0">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="m-0 font-weight-bold">Simpanan Khusus (Berjangka)</h5>
          <button className="btn btn-sm btn-success" onClick={() => setShowForm(true)}>
            <i className="fas fa-plus mr-1"></i> Pembukaan Baru
          </button>
        </div>

        <div className="card-body">
          {/* Filters */}
          <div className="row mb-3">
            <div className="col-md-3">
              <form onSubmit={handleSearch}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Cari No.Rek / Nama / NBA"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <div className="input-group-append">
                    <button className="btn btn-outline-secondary" type="submit">
                      <i className="fas fa-search"></i>
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={filterJenis}
                onChange={(e) => setFilterJenis(e.target.value)}
              >
                <option value="">Semua Tenor</option>
                {TENOR_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">Semua Status</option>
                <option value="AKTIF">Aktif</option>
                <option value="LUNAS">Lunas</option>
                <option value="DITUTUP">Ditutup</option>
              </select>
            </div>
            <div className="col-md-2">
              <input
                type="date"
                className="form-control"
                placeholder="Tgl Mulai"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <input
                type="date"
                className="form-control"
                placeholder="Tgl Akhir"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="col-md-1">
              <button className="btn btn-outline-primary btn-block" onClick={fetchAccounts}>
                <i className="fas fa-sync"></i>
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="alert alert-info mb-0 py-2">
                <small>Total Rekening: <strong>{accounts.length}</strong></small>
              </div>
            </div>
            <div className="col-md-4">
              <div className="alert alert-success mb-0 py-2">
                <small>Total Setoran: <strong>Rp {formatCurrency(accounts.reduce((sum, a) => sum + parseFloat(a.JumlahSetoran || 0), 0))}</strong></small>
              </div>
            </div>
            <div className="col-md-4">
              <div className="alert alert-warning mb-0 py-2">
                <small>Aktif: <strong>{accounts.filter(a => a.Status === 'AKTIF').length}</strong></small>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : accounts.length === 0 ? (
            <div className="alert alert-info text-center">
              Tidak ada data simpanan berjangka
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead className="thead-light">
                  <tr>
                    <th>No.Rekening</th>
                    <th>Nama Anggota</th>
                    <th>NBA</th>
                    <th>Tenor</th>
                    <th>Setoran</th>
                    <th>Bunga %</th>
                    <th>Tgl Setor</th>
                    <th>Jatuh Tempo</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.Id}>
                      <td><strong>{account.NoRekening}</strong></td>
                      <td>{account.Nama}</td>
                      <td>{account.NBA}</td>
                      <td>{TENOR_OPTIONS.find(t => t.value === account.KodeJenis)?.label || account.KodeJenis}</td>
                      <td className="text-right">Rp {formatCurrency(account.JumlahSetoran)}</td>
                      <td className="text-right">{account.BungaPersen}%</td>
                      <td>{formatDate(account.TanggalSetoran)}</td>
                      <td>{formatDate(account.TanggalJatuhTempo)}</td>
                      <td>
                        <span className={`badge ${STATUS_COLORS[account.Status] || 'badge-secondary'}`}>
                          {account.Status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleViewDetail(account)}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <SimpananBerjangkaForm
          onClose={() => setShowForm(false)}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Detail Modal */}
      {showDetail && selectedAccount && (
        <SimpananBerjangkaDetail
          account={selectedAccount}
          onClose={() => {
            setShowDetail(false)
            setSelectedAccount(null)
          }}
          onRefresh={fetchAccounts}
        />
      )}
    </div>
  )
}

export default SimpananBerjangka